import type { WorkflowGraphDensity } from "../../../api/userPreferencesApi";
import type { GraphNode, GraphPort } from "./workflowGraph";

/**
 * Where everything on a graph card sits, in graph pixels (processes.md P-14): its size,
 * the rows of its side ports, the tabs of its top inputs, the dots of its bottom outputs
 * and the open card's settings block. The layout sizes nodes with it, the card draws its
 * ports at these places, and every edge ends at an anchor from here, so an edge meets its
 * port whatever the zoom, the page's own scaling, or when the canvas was measured. Pure.
 *
 * Pipeline cards keep their title and key fact to fixed line counts, so their port rows
 * line up across the row; longer text is cut, with the full text in a tooltip.
 */

/** How much a collapsed card shows: the user's setting, kept with their account. */
export type GraphDensity = WorkflowGraphDensity;

export const CARD = {
  mainWidth: 192,
  inputWidth: 172,
  /** An open card shows its settings as readable lines. */
  openWidth: 380,
  border: 1,
  paddingTop: 8,
  paddingX: 12,
  /** Number, kind, chips, Edit and the chevron. */
  header: 20,
  titleGap: 4,
  titleLine: 18,
  titleLines: 2,
  factGap: 2,
  factLine: 16,
  /** Above the line over the port rows. */
  portsGap: 8,
  /** Between that line and the first row. */
  portsInset: 4,
  portRow: 20,
  portsBottom: 4,
  paddingBottom: 8,
  /** Where an edge ends on a side port: just outside its mark, an input's 11 px circle. */
  dotRadius: 5.5,
  tab: {
    height: 20,
    /** How far a tab stands above the card's top edge; its edge ends at the tab's top. */
    rise: 11,
    /** An estimate of the tab's text width at 11 px, so neighbouring tabs never overlap. */
    characterWidth: 6,
    padding: 14,
    gap: 4,
  },
  /** The open card's settings block, by kind; its text scrolls inside. */
  details: {
    agent: 300,
    human: 190,
    tool: 210,
    sub_workflow: 130,
    artifact: 120,
    selection: 120,
    terms: 140,
    item: 100,
    abstract_step: 100,
    unreadable: 80,
  },
  /** The row of links above an open artifact card's settings: the Workflow and Thread that made it. */
  actions: 40,
  /**
   * The words a card must not cut: its kind and chips in the header, its port names in the
   * rows. A card is widened for them, estimated generously at 12 px, by at most `maxWiden`.
   * A header that is still too long puts its chips on a second line; longer port names are
   * cut, with the full text in their tooltip.
   */
  words: {
    characterWidth: 6.4,
    /** Chips are set at 11 px. */
    chipCharacterWidth: 5.8,
    /** A chip's border and padding, and the gap before it. */
    chip: 20,
    /** The chevron, and the gap before it; the Edit button takes as much. */
    chevron: 28,
    /** A kind mark's 14 px icon and the gap beside it. */
    icon: 18,
    /** Between a row's two names. */
    rowGap: 16,
    /** Characters a line leaves for a word that wraps whole to the next. */
    wrapSlack: 8,
    maxWiden: 104,
  },
} as const;

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** A side port's row: its label sits beside its anchor. */
export interface PortRow {
  readonly port: GraphPort;
  readonly y: number;
  /** Its label's width, padding included: the card's, or its share of a row both sides use. */
  readonly width: number;
}

export interface PortTab {
  readonly port: GraphPort;
  /** The tab's centre. */
  readonly x: number;
  readonly width: number;
}

export interface BottomDot {
  readonly port: GraphPort;
  readonly x: number;
}

export interface CardGeometry {
  readonly width: number;
  readonly height: number;
  /** One: number, kind, chips, Edit and chevron in a line. Two: the chips on a line of their own. */
  readonly headerLines: 1 | 2;
  /** How many lines the key fact takes; 0 when the card shows none. */
  readonly factLines: number;
  /** Of those, the lines its fact (or on the run graph its line) shows; the rest say what it does. */
  readonly factShown: number;
  /** The line over the port rows; `null` without side ports. */
  readonly portsTop: number | null;
  readonly left: readonly PortRow[];
  readonly right: readonly PortRow[];
  readonly tabs: readonly PortTab[];
  readonly bottom: readonly BottomDot[];
  /** The open card's settings block, its row of links included; `null` while closed. */
  readonly details: { readonly top: number; readonly height: number } | null;
  /** Where each port's edge ends or starts, by port ID, relative to the card's top left. */
  readonly anchors: ReadonlyMap<string, Point>;
  /** The height at which `then` arrows meet the card's sides: the header's (first line's) middle. */
  readonly thenY: number;
}

export interface CardOptions {
  readonly density: GraphDensity;
  readonly open: boolean;
  /** The card's words, in characters, so that it is wide enough for them (`CARD.words`). */
  readonly words?: CardWords;
}

export interface CardWords {
  /** The header's text: number and kind, and each chip's text. */
  readonly header: number;
  /** The icons beside the kind's word: its kind's, and the raised hand when shown (`StepKindMark`). */
  readonly icons?: number;
  readonly chips: readonly number[];
  readonly chevron: boolean;
  /** Its Edit button stands in the header (F51). */
  readonly edit?: boolean;
  /** Each port's words, purpose and type, by port ID. */
  readonly ports: ReadonlyMap<string, number>;
  /** On the run graph: the characters of the card's line (what it waits for, or made), which takes a third line when two can't hold it (taste r2, T1-1). */
  readonly line?: number;
}

/** The key fact's lines on a collapsed card: none, one fact, or the fact and what it does. */
export function factLines(node: GraphNode, density: GraphDensity): number {
  if (density === "compact") {
    return 0;
  }
  return density === "detailed" && node.band === "main" && node.kind === "step" ? 4 : 2;
}

const BODY_TOP = CARD.border + CARD.paddingTop;

/** Where `then` arrows meet a card's or a frame's sides: its header's middle. */
export const THEN_Y = BODY_TOP + CARD.header / 2;

/** The geometry of a card: any node drawn as a card, open or closed (not an open frame). */
export function cardGeometry(node: GraphNode, options: CardOptions): CardGeometry {
  const base = factLines(node, options.density);
  const left = node.ports.filter((port) => port.side === "left");
  const right = node.ports.filter((port) => port.side === "right");
  const top = node.ports.filter((port) => port.side === "top");
  const bottom = node.ports.filter((port) => port.side === "bottom");
  const baseWidth = options.open ? CARD.openWidth : node.band === "main" ? CARD.mainWidth : CARD.inputWidth;
  const needs = options.words === undefined ? { width: baseWidth, headerLines: 1 as const } : wordsNeed(options.words, left, right, baseWidth + CARD.words.maxWiden);
  const tabWidths = top.map((port) => tabWidth(options.words?.ports.get(port.id) ?? port.name.length));
  const width = Math.max(baseWidth, needs.width, tabWidths.reduce((sum, tab) => sum + tab, 0) + CARD.tab.gap * (top.length + 1));
  // On the run graph a card's line takes a third line when two can't hold it at the card's width (taste r2 T1-1): the
  // card grows down, never wider, so a narrow pane beside the Run still frames it (taste r3 T3-1).
  const perLine = Math.floor((width - 2 * CARD.paddingX) / CARD.words.characterWidth);
  const line = options.words?.line;
  const longLine = base > 0 && line !== undefined && line > 2 * perLine - CARD.words.wrapSlack;
  const lines = base + (longLine ? 1 : 0);
  const body = BODY_TOP + CARD.header * needs.headerLines + CARD.titleGap + CARD.titleLine * CARD.titleLines + (lines === 0 ? 0 : CARD.factGap + CARD.factLine * lines);
  const rows = Math.max(left.length, right.length);
  const portsTop = rows === 0 ? null : body + CARD.portsGap;
  const rowY = (index: number) => (portsTop ?? 0) + CARD.portsInset + CARD.portRow * index + CARD.portRow / 2;
  const contentBottom = portsTop === null ? body : portsTop + CARD.portsInset + CARD.portRow * rows + CARD.portsBottom;
  // Only an artifact has links above its settings (the Workflow and Thread that made it); Edit is in the header.
  const detailsHeight = options.open ? CARD.details[detailsKind(node)] + (node.kind === "artifact" ? CARD.actions : 0) : 0;
  const details = options.open ? { top: contentBottom + CARD.portsBottom, height: detailsHeight } : null;
  const height = details === null ? contentBottom + CARD.paddingBottom + CARD.border : details.top + details.height + CARD.border;

  const anchors = new Map<string, Point>();
  const name = (port: GraphPort) => (options.words?.ports.get(port.id) ?? port.name.length) * CARD.words.characterWidth;
  const leftRows = left.map((port, index): PortRow => ({ port, y: rowY(index), width: rowWidth(name(port), right[index], name, width) }));
  const rightRows = right.map((port, index): PortRow => ({ port, y: rowY(index), width: rowWidth(name(port), left[index], name, width) }));
  for (const row of leftRows) {
    anchors.set(row.port.id, { x: -CARD.dotRadius, y: row.y });
  }
  for (const row of rightRows) {
    anchors.set(row.port.id, { x: width, y: row.y });
  }
  const tabs = spreadTabs(top, tabWidths, width);
  for (const tab of tabs) {
    anchors.set(tab.port.id, { x: tab.x, y: -CARD.tab.rise });
  }
  const dots = bottom.map((port, index): BottomDot => ({ port, x: (width * (index + 1)) / (bottom.length + 1) }));
  for (const dot of dots) {
    anchors.set(dot.port.id, { x: dot.x, y: height });
  }
  return { width, height, headerLines: needs.headerLines, factLines: lines, factShown: Math.min(base, 2) + (longLine ? 1 : 0), portsTop, left: leftRows, right: rightRows, tabs, bottom: dots, details, anchors, thenY: THEN_Y };
}

/**
 * The width a card's words need, up to `widest`, and its header's lines: the header's kind
 * and chips in one line if that fits, else the chips on a second line; and each row's port
 * names, a shared row holding both names side by side (`rowWidth`).
 */
function wordsNeed(words: CardWords, left: readonly GraphPort[], right: readonly GraphPort[], widest: number): { width: number; headerLines: 1 | 2 } {
  const { characterWidth, chipCharacterWidth, chip, chevron, icon, rowGap } = CARD.words;
  const kindLine = 2 * CARD.paddingX + words.header * characterWidth + (words.icons ?? 0) * icon + (words.chevron ? chevron : 0) + (words.edit === true ? chevron : 0);
  const chips = words.chips.reduce((sum, chars) => sum + chars * chipCharacterWidth + chip, 0);
  const oneLine = kindLine + chips;
  const headerLines = oneLine <= widest ? 1 : 2;
  const header = headerLines === 1 ? oneLine : Math.max(kindLine, 2 * CARD.paddingX + chips);
  const name = (port: GraphPort | undefined) => (port === undefined ? 0 : (words.ports.get(port.id) ?? 0) * characterWidth);
  const rows = Array.from({ length: Math.max(left.length, right.length) }, (_, index) => {
    const [input, output] = [left[index], right[index]];
    return input !== undefined && output !== undefined ? name(input) + name(output) + 2 * CARD.paddingX + rowGap : Math.max(name(input), name(output)) + 2 * CARD.paddingX;
  });
  return { width: Math.min(widest, Math.ceil(Math.max(header, ...rows))), headerLines };
}

/**
 * A side row's label width: the whole card, or, when the other side has a port in the same
 * row, a share by what each name needs, so a long type is not cut to half the card (F40).
 */
function rowWidth(own: number, other: GraphPort | undefined, name: (port: GraphPort) => number, cardWidth: number): number {
  if (other === undefined) {
    return cardWidth;
  }
  const ownNeed = own + CARD.paddingX;
  const otherNeed = name(other) + CARD.paddingX;
  return ((cardWidth - CARD.words.rowGap) * ownNeed) / (ownNeed + otherNeed);
}

/** Where an open frame's ports sit: its outputs spread along its bottom edge. */
export function frameAnchors(node: GraphNode, width: number, height: number): ReadonlyMap<string, Point> {
  const bottom = node.ports.filter((port) => port.side === "bottom");
  return new Map(bottom.map((port, index) => [port.id, { x: (width * (index + 1)) / (bottom.length + 1), y: height }]));
}

/** A tab's width from its words' characters, estimated generously. */
function tabWidth(characters: number): number {
  return Math.ceil(characters * CARD.tab.characterWidth + CARD.tab.padding);
}

/** Tabs spread along the top edge with equal gaps, in the ports' order. */
function spreadTabs(ports: readonly GraphPort[], widths: readonly number[], cardWidth: number): PortTab[] {
  const free = cardWidth - widths.reduce((sum, width) => sum + width, 0);
  const gap = free / (ports.length + 1);
  let cursor = gap;
  return ports.map((port, index) => {
    const width = widths[index] ?? 0;
    const tab = { port, x: cursor + width / 2, width };
    cursor += width + gap;
    return tab;
  });
}

function detailsKind(node: GraphNode): keyof typeof CARD.details {
  if (node.kind === "step") {
    // A talk step is offered by a review step, never placed in a Workflow (F105).
    return node.step === null || node.step.kind === "talk" ? "unreadable" : node.step.kind;
  }
  return node.kind;
}
